import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocale } from "../contexts/LocaleContext";
import {
  Container,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Link,
  Collapse,
  IconButton,
  Button,
  ButtonGroup,
  TextField,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { useAuth } from "../contexts/AuthContext";
import { getTeamReports, getUserProfile } from "../utils/firebase";
import CircularGauge from "../components/CircularGauge";


function formatDate(timestamp) {
  if (!timestamp) return "—";
  const d = new Date(timestamp);
  return d.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function toISODate(d) {
  return d.toISOString().slice(0, 10);
}

const PRESET_KEYS = ["preset7", "preset30", "preset90", "presetAll"];
const PRESET_DAYS = [7, 30, 90, null];

function getDefaultDates() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return { startDate: toISODate(start), endDate: toISODate(end) };
}

export default function ReportsPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { localizePath } = useLocale();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [expandedRep, setExpandedRep] = useState(null);
  const [dateRange, setDateRange] = useState(getDefaultDates);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getTeamReports({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      setData(result.data);
      setError(null);
    } catch (err) {
      console.error("Failed to load reports:", err);
      setError(err.message || t("reportsPage.failedLoad"));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate, t]);

  const handlePreset = (days) => {
    const end = new Date();
    if (days == null) {
      setDateRange({ startDate: null, endDate: null });
    } else {
      const start = new Date();
      start.setDate(start.getDate() - days);
      setDateRange({ startDate: toISODate(start), endDate: toISODate(end) });
    }
  };

  const handleCustomDateChange = (field, value) => {
    setDateRange((prev) => ({ ...prev, [field]: value || null }));
  };

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    getUserProfile()
      .then((r) => {
        if (cancelled) return;
        const { teamRole } = r.data || {};
        if (teamRole !== "admin") {
          setRedirecting(true);
          navigate(localizePath("/"), { replace: true });
        } else {
          setRedirecting(false);
          loadReports();
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRedirecting(false);
          navigate(localizePath("/"), { replace: true });
        }
      });
    return () => { cancelled = true; };
  }, [user, navigate, loadReports, localizePath]);

  const handleViewCall = (memberId, callId) => {
    navigate(localizePath(`/call/${callId}?memberId=${memberId}`));
  };

  if (redirecting || (loading && !data)) {
    return (
      <Box display="flex" justifyContent="center" py={10}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Box sx={{ bgcolor: "#ffffff", minHeight: "100vh" }}>
        <Container maxWidth="lg" sx={{ py: 5 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: "#111827", mb: 2 }}>
            {t("reportsPage.title")}
          </Typography>
          <Typography color="error">{error}</Typography>
        </Container>
      </Box>
    );
  }

  const { teamName, reps = [], commonObjections = [], coachingPriorities = [], overview = {}, dateRange: responseDateRange } = data || {};
  const { totalTeamCalls = 0, teamAvgScore = null } = overview;

  const dateRangeLabel = responseDateRange?.startDate && responseDateRange?.endDate
    ? `${new Date(responseDateRange.startDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} – ${new Date(responseDateRange.endDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
    : t("reportsPage.allTime");

  return (
    <Box sx={{ bgcolor: "#ffffff", minHeight: "100vh" }}>
      <Container maxWidth="lg" sx={{ py: 5, px: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" gap={1} mb={3}>
          <AssessmentIcon sx={{ fontSize: 28, color: "#6366f1" }} />
          <Typography
            variant="h5"
            sx={{
              fontFamily: '"Cal Sans", "Inter", sans-serif',
              fontWeight: 600,
              color: "#111827",
            }}
          >
            {t("reportsPage.title")}
          </Typography>
          {teamName && (
            <Typography sx={{ color: "#6b7280", fontSize: 14 }}>
              — {teamName}
            </Typography>
          )}
        </Box>

        {/* Date range filter */}
        <Box sx={{ mb: 4 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#6b7280", mb: 1, textTransform: "uppercase" }}>
            {t("reportsPage.dateRange")}
          </Typography>
          <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
            <ButtonGroup size="small" variant="outlined">
              {PRESET_KEYS.map((key, i) => (
                <Button
                  key={key}
                  onClick={() => handlePreset(PRESET_DAYS[i])}
                  sx={{ textTransform: "none" }}
                >
                  {t(`reportsPage.${key}`)}
                </Button>
              ))}
            </ButtonGroup>
            <Box display="flex" alignItems="center" gap={1}>
              <TextField
                size="small"
                type="date"
                label={t("reportsPage.from")}
                value={dateRange.startDate || ""}
                onChange={(e) => handleCustomDateChange("startDate", e.target.value || null)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
              <TextField
                size="small"
                type="date"
                label={t("reportsPage.to")}
                value={dateRange.endDate || ""}
                onChange={(e) => handleCustomDateChange("endDate", e.target.value || null)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
            </Box>
            <Typography sx={{ fontSize: 14, color: "#6b7280" }}>
              {t("reportsPage.showing")}: {dateRangeLabel}
            </Typography>
          </Box>
        </Box>

        {/* Overview */}
        <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 4 }}>
          <Paper sx={{ p: 2, minWidth: 140, flex: 1 }}>
            <Typography sx={{ color: "#6b7280", fontSize: 12, fontWeight: 600, textTransform: "uppercase" }}>
              {t("reportsPage.totalTeamCalls")}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, color: "#111827" }}>
              {totalTeamCalls}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, minWidth: 140, flex: 1 }}>
            <Typography sx={{ color: "#6b7280", fontSize: 12, fontWeight: 600, textTransform: "uppercase" }}>
              {t("reportsPage.teamAvgScore")}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, color: "#111827" }}>
              {teamAvgScore != null ? teamAvgScore : "—"}
            </Typography>
          </Paper>
        </Box>

        {/* Rep Insights Table */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#111827", mb: 2 }}>
          {t("reportsPage.repInsights")}
        </Typography>
        <TableContainer
          component={Paper}
          sx={{
            border: "1px solid #e5e7eb",
            borderRadius: 1,
            overflow: "hidden",
            mb: 4,
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f9fafb" }}>
                <TableCell sx={{ fontWeight: 600, color: "#111827" }}></TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#111827" }}>{t("reportsPage.member")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#111827" }} align="right">
                  {t("reportsPage.avgScore")}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#111827" }} align="right">
                  {t("reportsPage.calls")}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#111827" }}>{t("reportsPage.weakestArea")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#111827" }} align="right">
                  {t("reportsPage.nextStepsPct")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reps.map((rep) => {
                const isExpanded = expandedRep === rep.userId;
                const hasRecentCalls = rep.recentCalls?.length > 0;
                const isCurrentUser = rep.userId === user?.uid;
                return (
                  <React.Fragment key={rep.userId}>
                    <TableRow
                      key={rep.userId}
                      sx={{
                        "&:hover": { bgcolor: "#f9fafb" },
                      }}
                    >
                      <TableCell sx={{ width: 40 }}>
                        {hasRecentCalls && (
                          <IconButton
                            size="small"
                            onClick={() => setExpandedRep(isExpanded ? null : rep.userId)}
                          >
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ color: "#111827", fontWeight: isCurrentUser ? 600 : 400 }}>
                          {rep.email}
                          {isCurrentUser && (
                            <Typography component="span" sx={{ color: "#6b7280", fontSize: 12, ml: 1 }}>
                              {t("reportsPage.you")}
                            </Typography>
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {rep.avgScore != null ? (
                          <Box display="inline-flex" justifyContent="flex-end" width="100%">
                            <CircularGauge score={rep.avgScore} size={32} strokeWidth={2.5} />
                          </Box>
                        ) : "—"}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "#6b7280" }}>
                        {rep.totalCalls}
                      </TableCell>
                      <TableCell>
                        {rep.weakestDimension ? (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <TrendingDownIcon sx={{ fontSize: 16, color: "#dc2626" }} />
                            <Typography sx={{ fontSize: 14, color: "#374151" }}>
                              {rep.weakestDimension} {rep.weakestScore != null && `(${rep.weakestScore})`}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography sx={{ color: "#9ca3af", fontSize: 14 }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "#6b7280" }}>
                        {rep.nextStepsRate != null ? `${rep.nextStepsRate}%` : "—"}
                      </TableCell>
                    </TableRow>
                    {hasRecentCalls && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0, borderBottom: 0 }}>
                          <Collapse in={isExpanded}>
                            <Box sx={{ bgcolor: "#f9fafb", p: 2, borderBottom: "1px solid #e5e7eb" }}>
                              <Typography sx={{ fontWeight: 600, fontSize: 12, color: "#6b7280", mb: 1 }}>
                                {t("reportsPage.recentCalls")}
                              </Typography>
                              <List dense disablePadding>
                                {rep.recentCalls.map((c) => (
                                  <ListItem
                                    key={c.id}
                                    dense
                                    sx={{
                                      py: 0.5,
                                      cursor: "pointer",
                                      "&:hover": { bgcolor: "#f3f4f6", borderRadius: 1 },
                                    }}
                                    onClick={() => handleViewCall(rep.userId, c.id)}
                                  >
                                    <ListItemText
                                      primary={c.title}
                                      secondary={formatDate(c.timestamp) + (c.score != null ? ` • Score: ${c.score}` : "")}
                                      primaryTypographyProps={{ fontSize: 14 }}
                                      secondaryTypographyProps={{ fontSize: 12, color: "#6b7280" }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Common Objections */}
        {commonObjections.length > 0 && (
          <>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#111827", mb: 2 }}>
              {t("reportsPage.commonObjections")}
            </Typography>
            <TableContainer
              component={Paper}
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: 1,
                overflow: "hidden",
                mb: 4,
                maxWidth: 400,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f9fafb" }}>
                    <TableCell sx={{ fontWeight: 600, color: "#111827" }}>{t("reportsPage.category")}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#111827" }} align="right">
                      {t("reportsPage.count")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {commonObjections.map((obj) => (
                    <TableRow key={obj.category} sx={{ "&:hover": { bgcolor: "#f9fafb" } }}>
                      <TableCell sx={{ color: "#374151" }}>{t(`reportsPage.objections.${obj.category}`, { defaultValue: obj.category })}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: "#111827" }}>
                        {obj.count}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Coaching Priorities */}
        {coachingPriorities.length > 0 && (
          <>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#111827", mb: 2 }}>
              {t("reportsPage.coachingPriorities")}
            </Typography>
            <Box display="flex" flexDirection="column" gap={1} sx={{ mb: 4 }}>
              {coachingPriorities.map((p) => {
                const isCoachExpanded = expandedRep === p.userId;
                return (
                  <Card key={p.userId} variant="outlined" sx={{ borderColor: "#e5e7eb" }}>
                    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                      <Typography sx={{ fontWeight: 600, color: "#111827" }}>
                        {p.email} — needs work on {p.weakestDimension}
                        {p.weakestScore != null && ` (${p.weakestScore})`}
                      </Typography>
                      {p.topImprovementTips?.length > 0 && (
                        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2, fontSize: 14, color: "#6b7280" }}>
                          {p.topImprovementTips.map((tip, j) => (
                            <li key={j}>{tip}</li>
                          ))}
                        </Box>
                      )}
                      {reps.find((r) => r.userId === p.userId)?.recentCalls?.length > 0 && (
                        <Link
                          component="button"
                          variant="body2"
                          sx={{ mt: 0.5, cursor: "pointer" }}
                          onClick={() => setExpandedRep(isCoachExpanded ? null : p.userId)}
                        >
                          {isCoachExpanded ? t("reportsPage.hideCalls") : t("reportsPage.viewRecentCalls")}
                        </Link>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </>
        )}

        {reps.length === 0 && !loading && (
          <Typography sx={{ color: "#6b7280" }}>
            {t("reportsPage.noTeamData")}
          </Typography>
        )}

        {reps.length > 0 && totalTeamCalls === 0 && !loading && (
          <Typography sx={{ color: "#6b7280" }}>
            {t("reportsPage.noCallsInRange")}
          </Typography>
        )}
      </Container>
    </Box>
  );
}
